import React, { useRef, useEffect, ReactNode } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { CloseIcon, Css, Responsive } from '../../../../ui';
import { Color } from '../../../../styles/Color';
import { Z_INDEX_DIALOG } from '../../../../styles/zIndex';

interface IDialogProps {
	isOpen: boolean;
	onClose: () => void;
	mobileVariant?: 'default' | 'fullScreen';
	children: ReactNode;
}

export default function Dialog({
	isOpen,
	onClose,
	mobileVariant = 'default',
	children,
}: IDialogProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const useFullScreenOnMobile = mobileVariant === 'fullScreen';

	useEffect(() => {
		const dialog = dialogRef.current;

		if (isOpen) {
			dialog?.showModal();
			document.body.style.overflow = 'hidden';
		} else {
			dialog?.close();
			document.body.style.overflow = '';
		}

		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen, dialogRef]);

	const handleClose = () => {
		onClose();
	};

	return (
		<StyledDialog
			ref={dialogRef}
			onClose={handleClose}
			className="dialog"
			useFullScreenOnMobile={useFullScreenOnMobile && isOpen}
		>
			{useFullScreenOnMobile && isOpen && (
				<FullScreenMobileCloseButton onClick={handleClose}>
					<CloseIcon aria-label="Close dialog" />
				</FullScreenMobileCloseButton>
			)}

			<DialogContent useFullScreenOnMobile={useFullScreenOnMobile}>
				<CloseButton
					onClick={handleClose}
					useFullScreenOnMobile={useFullScreenOnMobile}
				>
					<CloseIcon aria-label="Close dialog" />
				</CloseButton>
				{children}
			</DialogContent>
		</StyledDialog>
	);
}

const StyledDialog = styled.dialog<{
	useFullScreenOnMobile?: boolean;
}>`
	width: 80%;
	border-radius: 16px;
	border: none;
	padding: 0;
	height: max-content;
	z-index: ${Z_INDEX_DIALOG};
	position: fixed;
	overflow: hidden;
	background-color: transparent;

	::backdrop {
		background: rgba(0, 0, 0, 0.6);
	}

	@media (${Responsive.getMediaQueryForBreakpoint('desktopXL')}) {
		max-width: 70%;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		width: 100%;
	}

	// mobile landscape
	@media only screen and (max-height: 576px) and (orientation: landscape) {
		height: auto;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		${({ useFullScreenOnMobile }) =>
			useFullScreenOnMobile &&
			css`
				width: 100vw;
				max-width: 100vw;
				height: 100vh;
				border-radius: 0;
				margin: 0;
			`}

		::backdrop {
			${({ useFullScreenOnMobile }) =>
				useFullScreenOnMobile &&
				css`
					background: ${Color.Neutral1};
				`}
		}
	}
`;

const DialogContent = styled.div<{ useFullScreenOnMobile: boolean }>`
	position: relative;

	${({ useFullScreenOnMobile }) =>
		useFullScreenOnMobile &&
		css`
			@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
				display: flex;
				align-items: center;
				min-height: 100vh;
			}
		`}
`;

const CloseButton = styled.button<{ useFullScreenOnMobile?: boolean }>`
	border: none;
	background: none;
	padding: 0;
	cursor: pointer;
	height: 40px;
	width: 40px;
	background-color: ${Color.Neutral2};
	border-radius: 8px;
	position: absolute;
	right: ${Css.pxToRem(24)};
	top: ${Css.pxToRem(24)};
	z-index: ${Z_INDEX_DIALOG};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		${({ useFullScreenOnMobile }) =>
			useFullScreenOnMobile &&
			css`
				display: none;
			`}
	}
`;

const FullScreenMobileCloseButton = styled(CloseButton)`
	display: none;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		display: block;
	}
`;
